import { Skeleton, Typography } from '@/components/atoms';
import type { FormPickerOption } from '@/components/molecules/FormPicker';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useTranslation, useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { useI18nContext } from '@/core/providers/I18nProvider';
import {
    OPTION_PICKER_SHEET_ROW_HEIGHT,
    useScrollOptionPickerToSelected,
} from '@/components/molecules/optionPicker/useScrollOptionPickerToSelected';
import React, { useRef } from 'react';
import {
    FlatList,
    Modal,
    Platform,
    Pressable,
    StyleSheet,
    TouchableOpacity,
    View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { OptionPickerModalProps } from './OptionPickerModal.types';

/**
 * Mobile bottom-sheet picker (iOS, Android, and compact web).
 */
export function OptionPickerBottomSheet({
    visible,
    title,
    options,
    selectedKey,
    onSelect,
    onClose,
    loading = false,
}: Readonly<OptionPickerModalProps>) {
    const colors = useUIColors();
    const { isRTL } = useI18nContext();
    // Native Modal content doesn't reliably inherit the root direction:'rtl',
    // so textAlign alone flips unpredictably. Pinning direction:'ltr' on the
    // header/rows (same approach as Accordion) makes 'right' always mean the
    // physical right edge. Web is unchanged: isRTL ? 'right' : 'left'.
    const isMobileRTL = Platform.OS !== 'web' && isRTL;
    const textAlign = isRTL ? 'right' : 'left';
    const { t } = useTranslation();
    const insets = useSafeAreaInsets();
    const listRef = useRef<FlatList<FormPickerOption>>(null);
    const { getItemLayout, onScrollToIndexFailed } = useScrollOptionPickerToSelected({
        listRef,
        visible,
        loading,
        options,
        selectedKey,
        rowHeight: OPTION_PICKER_SHEET_ROW_HEIGHT,
    });

    return (
        <Modal
            visible={visible}
            animationType="slide"
            transparent
            onRequestClose={onClose}
        >
            <Pressable
                style={styles.modalBackdrop}
                onPress={onClose}
                accessibilityRole="button"
            />
            <View
                style={[styles.modalCard, { backgroundColor: colors.backgroundLevel1 }]}
            >
                <View style={[styles.modalHeader, isMobileRTL && styles.nativeLtrBox]}>
                    <Typography
                        variant="headline"
                        color="primary"
                        style={{ textAlign }}
                    >
                        {title}
                    </Typography>
                </View>
                <FlatList
                    ref={listRef}
                    data={loading ? Array.from({ length: 6 }) : options}
                    keyExtractor={(item, idx) =>
                        (item as FormPickerOption)?.key ||
                        (item as FormPickerOption)?.id ||
                        `skeleton-${idx}`
                    }
                    style={styles.modalList}
                    contentContainerStyle={[
                        styles.modalListContent,
                        Platform.OS !== 'web' && {
                            paddingBottom: Math.max(insets.bottom, 24),
                        },
                    ]}
                    showsVerticalScrollIndicator
                    getItemLayout={loading ? undefined : getItemLayout}
                    onScrollToIndexFailed={onScrollToIndexFailed}
                    ListEmptyComponent={
                        <View style={styles.modalEmpty}>
                            <Typography
                                variant="body"
                                color="tertiary"
                                style={{ textAlign: 'center' }}
                            >
                                {t('support.emptyState')}
                            </Typography>
                        </View>
                    }
                    renderItem={({ item }) => {
                        if (loading) {
                            return (
                                <Skeleton
                                    height={48}
                                    borderRadius={8}
                                    style={{ marginBottom: 8 }}
                                />
                            );
                        }
                        const option = item as FormPickerOption;
                        const itemKey = option.key || option.id;
                        const isSelected = itemKey === selectedKey;
                        return (
                            <TouchableOpacity
                                activeOpacity={0.7}
                                style={[
                                    styles.modalItem,
                                    {
                                        flexDirection: 'row',
                                        backgroundColor: isSelected
                                            ? colors.backgroundLevel3
                                            : colors.backgroundLevel1,
                                    },
                                    isMobileRTL && styles.nativeLtrBox,
                                ]}
                                onPress={() => {
                                    trackCatalogEvent({
                                        screen: DT_SCREEN.SERVICE_REQUEST_CREATE,
                                        actionName: 'service_request_option_picker_select',
                                        eventType: 'action',
                                    });
                                    onSelect(option);
                                }}
                            >
                                <Typography
                                    variant="body"
                                    color="primary"
                                    style={{ flex: 1, textAlign }}
                                >
                                    {option.label}
                                </Typography>
                            </TouchableOpacity>
                        );
                    }}
                />
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    nativeLtrBox: {
        direction: 'ltr',
    },
    modalBackdrop: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.3)',
    },
    modalCard: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        maxHeight: '60%',
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        paddingTop: 16,
        ...Platform.select({
            android: {
                elevation: 6,
            },
            ios: {
                shadowColor: '#000',
                shadowOpacity: 0.1,
                shadowRadius: 6,
                shadowOffset: { width: 0, height: -2 },
            },
            default: {},
        }),
    },
    modalHeader: {
        paddingHorizontal: 20,
        paddingBottom: 12,
    },
    modalList: {
        maxHeight: Platform.OS === 'web' ? '70%' : undefined,
    },
    modalListContent: {
        paddingHorizontal: 12,
        paddingBottom: Platform.OS === 'web' ? 24 : undefined,
        gap: 8,
    },
    modalEmpty: {
        paddingVertical: 24,
        paddingHorizontal: 12,
        alignItems: 'center',
    },
    modalItem: {
        paddingHorizontal: 12,
        paddingVertical: 14,
        borderRadius: 10,
    },
});
